import { effortMinutes } from './activity.js'
import { addDays, isoWeekKey, startOfDay, startOfWeek } from './date.js'
import { isWeekAchieved, summarizeWeek } from './goals.js'
import type { Activity, ActivityType } from './types.js'

/**
 * Les quêtes donnent une raison d'ouvrir l'app AUJOURD'HUI plutôt que dimanche.
 * C'est la réponse directe au trou de motivation entre deux séances.
 *
 * Elles sont générées de façon DÉTERMINISTE à partir de la date : deux
 * appareils, ou l'app et le serveur, produisent exactement les mêmes quêtes
 * sans avoir à les stocker ni à les synchroniser.
 */
export type QuestScope = 'daily' | 'weekly'

export type QuestKind =
  | 'move_today'
  | 'effort_today'
  | 'activity_today'
  | 'sessions_week'
  | 'variety_week'
  | 'reach_goal'
  | 'active_days'
  | 'long_session'

export interface Quest {
  id: string
  scope: QuestScope
  kind: QuestKind
  /** Valeur à atteindre (minutes, séances, jours… selon le kind) */
  target: number
  /** Renseigné pour `activity_today` */
  activityType?: ActivityType
  reward: number
  progress: number
  done: boolean
}

export interface QuestContext {
  activities: readonly Activity[]
  weeklyGoalMin: number
  favoriteActivities?: readonly ActivityType[]
  now?: Date
}

/** FNV-1a : petit, stable, et suffisant pour tirer des quêtes reproductibles. */
function hash(seed: string): number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h)
}

/** Tirage sans remise, déterministe pour une graine donnée. */
function pick<T>(pool: readonly T[], count: number, seed: string): T[] {
  const remaining = [...pool]
  const chosen: T[] = []
  for (let i = 0; i < count && remaining.length > 0; i++) {
    const index = hash(`${seed}:${i}`) % remaining.length
    chosen.push(remaining.splice(index, 1)[0]!)
  }
  return chosen
}

function inRange(activities: readonly Activity[], from: Date, to: Date): Activity[] {
  return activities.filter((activity) => {
    const date = new Date(activity.startedAt)
    return date >= from && date < to
  })
}

/** Plus longue série de jours consécutifs avec au moins une séance, cette semaine. */
function longestActiveRun(activities: readonly Activity[], weekStart: Date): number {
  const activeDays = new Set(
    activities.map((activity) => startOfDay(new Date(activity.startedAt)).getTime()),
  )
  let best = 0
  let run = 0
  for (let day = 0; day < 7; day++) {
    if (activeDays.has(addDays(weekStart, day).getTime())) {
      run += 1
      best = Math.max(best, run)
    } else {
      run = 0
    }
  }
  return best
}

const DAILY_POOL: Array<Pick<Quest, 'kind' | 'target' | 'reward'>> = [
  { kind: 'move_today', target: 15, reward: 20 },
  { kind: 'move_today', target: 30, reward: 35 },
  { kind: 'effort_today', target: 40, reward: 40 },
  { kind: 'activity_today', target: 1, reward: 30 },
]

const WEEKLY_POOL: Array<Pick<Quest, 'kind' | 'target' | 'reward'>> = [
  { kind: 'sessions_week', target: 3, reward: 60 },
  { kind: 'sessions_week', target: 4, reward: 80 },
  { kind: 'variety_week', target: 2, reward: 60 },
  { kind: 'variety_week', target: 3, reward: 90 },
  { kind: 'active_days', target: 3, reward: 80 },
  { kind: 'long_session', target: 45, reward: 50 },
  { kind: 'reach_goal', target: 1, reward: 100 },
]

export function dailyQuests(context: QuestContext): Quest[] {
  const now = context.now ?? new Date()
  const dayStart = startOfDay(now)
  const today = inRange(context.activities, dayStart, addDays(dayStart, 1))
  const seed = `d:${dayStart.toISOString().slice(0, 10)}`
  const favorites = context.favoriteActivities ?? []

  return pick(DAILY_POOL, 2, seed).map((template, index) => {
    const activityType =
      template.kind === 'activity_today' && favorites.length > 0
        ? favorites[hash(`${seed}:type:${index}`) % favorites.length]
        : undefined

    let progress = 0
    switch (template.kind) {
      case 'move_today':
        progress = today.reduce((sum, a) => sum + a.durationMin, 0)
        break
      case 'effort_today':
        progress = Math.round(today.reduce((sum, a) => sum + effortMinutes(a), 0))
        break
      case 'activity_today':
        progress = today.filter((a) => !activityType || a.type === activityType).length
        break
      default:
        progress = 0
    }

    return {
      id: `${seed}:${index}:${template.kind}:${template.target}`,
      scope: 'daily' as const,
      ...template,
      ...(activityType ? { activityType } : {}),
      progress,
      done: progress >= template.target,
    }
  })
}

export function weeklyQuests(context: QuestContext): Quest[] {
  const now = context.now ?? new Date()
  const weekStart = startOfWeek(now)
  const week = inRange(context.activities, weekStart, addDays(weekStart, 7))
  const seed = `w:${isoWeekKey(now)}`
  const summary = summarizeWeek(context.activities, weekStart, { now })

  return pick(WEEKLY_POOL, 3, seed).map((template, index) => {
    let progress = 0
    switch (template.kind) {
      case 'sessions_week':
        progress = week.length
        break
      case 'variety_week':
        progress = new Set(week.map((a) => a.type)).size
        break
      case 'active_days':
        progress = longestActiveRun(week, weekStart)
        break
      case 'long_session':
        progress = week.reduce((max, a) => Math.max(max, a.durationMin), 0)
        break
      case 'reach_goal':
        progress = isWeekAchieved(summary, context.weeklyGoalMin) ? 1 : 0
        break
      default:
        progress = 0
    }

    return {
      id: `${seed}:${index}:${template.kind}:${template.target}`,
      scope: 'weekly' as const,
      ...template,
      progress,
      done: progress >= template.target,
    }
  })
}

export function allQuests(context: QuestContext): Quest[] {
  return [...dailyQuests(context), ...weeklyQuests(context)]
}

/** Points déjà gagnés sur les quêtes en cours. */
export function questPoints(quests: readonly Quest[]): number {
  return quests.filter((quest) => quest.done).reduce((sum, q) => sum + q.reward, 0)
}
